import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Eye, Edit2, MapPin, CheckCircle2, XCircle, X, RotateCcw, User,
  Phone, Calendar, Clock, Navigation, DollarSign, Save, Plus,
  Trash2, Search, ChevronLeft, ChevronRight, CheckCircle,
  ShieldCheck, CarTaxiFront, Plane, Home, Timer,
  ShieldCheckIcon,
  Mail,
  MessageCircle,
  KeyRound,
} from 'lucide-react';
import { cn } from '../utils/cn.js';
import toast from 'react-hot-toast';
import GoogleInput from './GoogleInput.js';
import APIENDPOINT from './config.js';
import moment from 'moment/moment.js';

export const BookingsTable = ({ admin = false, onRefreshStats, filter = 'all', currentAdmin }) => {
  const [bookings, setBookings] = useState([]);
  const [drivers, setDrivers] = useState([]); // List of drivers for assignment
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [bookingPoints, setBookingPoints] = useState([]);
  const [editFormData, setEditFormData] = useState({});
  const [pricingRules, setPricingRules] = useState(null);
  const user = JSON.parse(localStorage.getItem('user'));

  // Search and Pagination State
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedHours, setSelectedHours] = useState(0);
  const [selectedMinutes, setSelectedMinutes] = useState(10);
  const [otpInput, setOtpInput] = useState("");
  const [closingOtpInput, setClosingOtpInput] = useState("");

  const [showStartModal, setShowStartModal] = useState(false);
  const [showConfirmStart, setShowConfirmStart] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [showCloseTripModal, setShowCloseTripModal] = useState(false);
  const [showClosingOtpModal, setShowClosingOtpModal] = useState(false);

  const [editablePrice, setEditablePrice] = useState(0);
  const [useLoyalty, setUseLoyalty] = useState(false);
  const [pointsToUse, setPointsToUse] = useState(0);
  const [pointsBalance, setPointsBalance] = useState(0);

  const fetchBookings = async () => {
    try {
      const res = await fetch(`${APIENDPOINT}booking.php?action=list`);
      const data = await res.json();
      setBookings(data);
    } catch (error) { console.error(error); }
  };

  const fetchDrivers = async () => {
    try {
      const res = await fetch(`${APIENDPOINT}booking.php?action=getDrivers`);
      const data = await res.json();
      setDrivers(data);
    } catch (error) { console.error(error); }
  };

  useEffect(() => {
    fetchBookings();
    if (admin) fetchDrivers();
  }, [admin]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filter]);

  // --- Search & Filter Logic ---
  const filteredData = useMemo(() => {
    const today = moment().format('YYYY-MM-DD');
    const loggedInAdminId = currentAdmin?.id ? Number(currentAdmin.id) : 0;

    return bookings.filter(b => {
      const bStatus = b.status !== undefined ? Number(b.status) : -1;
      const bAssignedId = b.assigned_id !== undefined ? Number(b.assigned_id) : 0;
      const bDriverUserId = b.driver_user_id !== undefined ? Number(b.driver_user_id) : 0;

      let matchesTab = false;

      // 1. Tab Filtering Logic
      if (filter === 'all') {
        matchesTab = true; // Show everything
      }
      else if (filter === 'self') {
        // Show ONLY bookings where the assigned driver ID matches the logged-in admin
        matchesTab = bDriverUserId === loggedInAdminId;
      }
      else if (filter === 'pending') {
        matchesTab = bStatus === 0;
      }
      else if (filter === 'approved') {
        matchesTab = bStatus === 1;
      }
      else if (filter === 'rejected') {
        matchesTab = bStatus === 2;
      }
      else if (filter === 'ongoing') {
        matchesTab = b.journey_date === today;
      }
      else if (filter === 'assigned') {
        matchesTab = bAssignedId > 0;
      }
      else if (filter === 'unassigned') {
        matchesTab = bAssignedId === 0 && bStatus !== 2;
      }

      // 2. Search Logic (Applies to whatever tab is selected)
      const s = searchTerm.toLowerCase().trim();
      const matchesSearch = s === "" ||
        b.name?.toLowerCase().includes(s) ||
        b.phone_number?.includes(s) ||
        b.id?.toString().includes(s);

      return matchesTab && matchesSearch;
    });
  }, [bookings, filter, searchTerm, currentAdmin]);
  // --- Enhanced Detail Opener ---
  const openDetails = async (booking, mode = 'view') => {
    try {
      // If we are in 'self' mode, fetch the extra trip status info needed for the driver flow
      const endpoint = filter === 'self'
        ? `${APIENDPOINT}driverbooking.php?action=getBookingDetails&id=${booking.id}&driver_id=${currentAdmin.id}`
        : `${APIENDPOINT}booking.php?action=getDetail&id=${booking.id}`;

      const res = await fetch(endpoint);
      const data = await res.json();
      const bData = data.booking;

      const pointsForState = (data.points || []).map(p => ({
        id: p.id, address: p.points_address, lat: p.latitude, lng: p.longitude
      }));

      if (mode === 'edit') {
        // Edit logic...
        setEditFormData({ ...bData, driver_user_id: bData.driver_user_id || "0" });
        setBookingPoints(pointsForState);
        setIsEditModalOpen(true);
      } else {
        // View logic...
        setSelectedBooking(bData);
        setBookingPoints(pointsForState);

        // Crucial: Load prices and loyalty for the "Close Trip" driver flow
        if (filter === 'self') {
          setEditablePrice(bData.price);
          setPointsBalance(bData.user_balance || 0);
        }
      }
    } catch (error) { console.error(error); }
  };
  // --- Pagination Logic ---
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredData.slice(start, start + itemsPerPage);
  }, [filteredData, currentPage]);



  const addPoint = () => {
    setBookingPoints([...bookingPoints, { address: '', lat: 0, lng: 0 }]);
  };

  const removePoint = (index) => {
    setBookingPoints(bookingPoints.filter((_, i) => i !== index));
  };

  const handleUpdate = async () => {
    try {
      const response = await fetch(`${APIENDPOINT}booking.php?action=update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ formData: editFormData, points: bookingPoints })
      });
      if (response.ok) {
        toast.success("Update Successful");
        setIsEditModalOpen(false);
        fetchBookings();
        if (onRefreshStats) onRefreshStats();
      }
    } catch (error) { toast.error("Update Failed"); }
  };

  const handleStatus = async (id, status) => {
    const action = status === 1 ? 'approve' : status === 2 ? 'reject' : 'revoke';
    try {
      await fetch(`${APIENDPOINT}booking.php?action=${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, price: selectedBooking?.price || editFormData?.price })
      });
      fetchBookings();
      if (onRefreshStats) onRefreshStats();
      setSelectedBooking(null);
    } catch (error) { console.error(error); }
  };

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await fetch(`${APIENDPOINT}booking.php?action=getPricing`);
        const data = await response.json();
        setPricingRules(data);
      } catch (error) { console.error("Failed to fetch pricing:", error); }
    };
    fetchPricing();
  }, []);

  const calculateTieredFare = useCallback((miles) => {
    if (!pricingRules) return 0;
    const m = parseFloat(miles) || 0;
    const baseDist = parseFloat(pricingRules.base_distance || 2.0);
    const basePrice = parseFloat(pricingRules.base_price || 6.0);
    const ratePerMile = parseFloat(pricingRules.price_per_mile || 2.60);
    const step = parseFloat(pricingRules.step_distance || 0.5);
    const roundedMiles = Math.ceil(m / step) * step;
    if (roundedMiles <= baseDist) return basePrice;
    const extraMiles = roundedMiles - baseDist;
    return basePrice + (extraMiles * ratePerMile);
  }, [pricingRules]);

  const calculateEditDistanceAndPrice = useCallback(() => {
    if (!window.google || !editFormData.pickup_lat || !editFormData.drop_lat || !isEditModalOpen) return;
    const ds = new window.google.maps.DirectionsService();
    const wp = bookingPoints
      .filter(p => p.lat && p.lng)
      .map(p => ({ location: { lat: Number(p.lat), lng: Number(p.lng) }, stopover: true }));

    ds.route({
      origin: { lat: Number(editFormData.pickup_lat), lng: Number(editFormData.pickup_lng) },
      destination: { lat: Number(editFormData.drop_lat), lng: Number(editFormData.drop_lng) },
      waypoints: wp,
      travelMode: window.google.maps.TravelMode.DRIVING,
    }, (result, status) => {
      if (status === "OK") {
        let totalMeters = 0;
        result.routes[0].legs.forEach(leg => { totalMeters += leg.distance.value; });
        const miles = totalMeters / 1609.34;
        const finalPrice = calculateTieredFare(miles);
        setEditFormData(prev => ({ ...prev, distance: miles.toFixed(2), price: finalPrice.toFixed(2) }));
      }
    });
  }, [editFormData.pickup_lat, editFormData.pickup_lng, editFormData.drop_lat, editFormData.drop_lng, bookingPoints, isEditModalOpen, calculateTieredFare]);

  useEffect(() => { calculateEditDistanceAndPrice(); }, [calculateEditDistanceAndPrice]);

  const getTripStatusLabel = (val) => {
    const v = parseInt(val || 0);
    const statuses = {
      0: { label: "Not Started", color: "text-gray-400 bg-gray-50" },
      1: { label: "Ready to Pickup", color: "text-blue-600 bg-blue-50" },
      2: { label: "Trip Started", color: "text-orange-600 bg-orange-50" },
      3: { label: "Reached Drop", color: "text-purple-600 bg-purple-50" },
      4: { label: "Completed", color: "text-emerald-600 bg-emerald-50" }
    };
    return statuses[v] || statuses[0];
  };

  const refreshDetail = async () => {
    if (!selectedBooking) return;
    const res = await fetch(`${APIENDPOINT}driverbooking.php?action=getBookingDetails&id=${selectedBooking.id}&driver_id=${currentAdmin.id}`);
    const data = await res.json();
    setSelectedBooking(data.booking);
    setBookingPoints(data.points);
    setEditablePrice(data.booking.price);
    setPointsBalance(data.booking.user_balance || 0);
  };
  const handleStartJourney = async () => {
    setIsProcessing(true);
    const totalDuration = (selectedHours * 60) + selectedMinutes;
    const res = await fetch(`${APIENDPOINT}driverbooking.php?action=startJourney&driver_id=${user.id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        booking_id: selectedBooking.id,
        driver_id: currentAdmin.id,
        user_id: selectedBooking.user_id,
        duration: totalDuration,
        customer_name: selectedBooking.name,
        customer_phone: selectedBooking.phone_number,
        driver_name: currentAdmin.name
      })
    });
    const data = await res.json();
    if (data.status) {
      toast.success("Journey Started!");
      setShowConfirmStart(false);
      setShowStartModal(false);
      refreshDetail();
    }
    setIsProcessing(false);
  };

  const handleVerifyOTP = async () => {
    const res = await fetch(`${APIENDPOINT}driverbooking.php?action=verifyOTP&driver_id=${user.id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ booking_id: selectedBooking.id, otp: otpInput })
    });
    const data = await res.json();
    if (data.status) {
      toast.success("OTP Verified!");
      setShowOtpModal(false);
      refreshDetail();
    } else toast.error("Invalid OTP");
  };

  const handleCloseTripSubmit = async () => {
    if (isProcessing) return;

    // 1. Validation
    if (useLoyalty && pointsToUse > pointsBalance) {
      toast.error("Points exceed user balance");
      return;
    }

    setIsProcessing(true);

    try {
      // 2. Calculation (100 points = £1.00)
      const discount = useLoyalty ? (parseFloat(pointsToUse) / 100) : 0;
      const calculatedPrice = parseFloat(editablePrice) - discount;
      const finalPrice = Math.max(0, calculatedPrice).toFixed(2); // Ensure price isn't negative

      // 3. API Call
      const res = await fetch(`${APIENDPOINT}driverbooking.php?action=closeTrip&driver_id=${user.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_id: selectedBooking.id, // Fixed variable name
          final_price: finalPrice,
          points_used: useLoyalty ? pointsToUse : 0
        })
      });

      const data = await res.json();

      if (data.status) {
        toast.success("Closing OTP sent to customer");
        setShowCloseTripModal(false);

        // 4. Refresh local data to show the new state (Step 3: Awaiting Closing OTP)
        await refreshDetail();
      } else {
        toast.error(data.error || "Failed to send OTP");
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred while closing the trip");
    } finally {
      setIsProcessing(false);
    }
  };
  const handleVerifyClosingOTP = async () => {
    const res = await fetch(`${APIENDPOINT}driverbooking.php?action=verifyClosingOTP&driver_id=${user.id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ booking_id: selectedBooking.id, cotp: closingOtpInput })
    });
    if ((await res.json()).status) {
      toast.success("Trip Completed!");
      setShowClosingOtpModal(false);
      setSelectedBooking(null);
      fetchBookings();
      onRefreshStats();
    } else toast.error("Invalid OTP");
  };

  // --- RENDER DRIVER BUTTONS ---
  const renderDriverButtons = () => {
    const tripStep = parseInt(selectedBooking?.trip_status_val || 0);
    switch (tripStep) {
      case 0: return <button onClick={() => setShowStartModal(true)} className="flex-1 bg-indigo-600 text-white py-3 rounded-xl font-bold">Start Journey</button>;
      case 1: return <button onClick={() => setShowOtpModal(true)} className="flex-1 bg-orange-500 text-white py-3 rounded-xl font-bold">Enter Pickup OTP</button>;
      case 2: return <button onClick={() => setShowCloseTripModal(true)} className="flex-1 bg-rose-600 text-white py-3 rounded-xl font-bold">Close Trip</button>;
      case 3: return <button onClick={() => setShowClosingOtpModal(true)} className="flex-1 bg-emerald-600 text-white py-3 rounded-xl font-bold">Enter Closing OTP</button>;
      case 4: return <div className="flex-1 text-center py-3 bg-emerald-50 text-emerald-600 font-bold rounded-xl border border-emerald-200">TRIP COMPLETED ✅</div>;
      default: return null;
    }
  };


  return (
    <div className="space-y-4">
      <div className="relative group">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within:text-emerald-500 transition-colors" />
        <input
          type="text"
          placeholder="Search by name, phone, email or ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-12 pr-4 py-3 bg-white border border-gray-100 rounded-2xl shadow-sm outline-none focus:ring-2 focus:ring-emerald-500 transition-all text-sm"
        />
      </div>

      <div className="divide-y divide-gray-100">
        {paginatedData.length > 0 ? paginatedData.map((b) => (
          <div key={b.id} className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 hover:bg-gray-50 transition-colors bg-[#f1f1f1] border border-[#dddddd] rounded-[10px] mb-[10px]">

            <div className="flex flex-col min-w-[120px]">
              <span className="text-[10px] font-mono text-gray-400">#{b.id}</span>
              <span className="font-bold text-gray-900 truncate">{b.name}</span>
              <div className="mt-1 flex flex-col gap-1">
                <StatusBadge status={b.status} />
                {b.driver_name && (
                  <span className="flex items-center gap-1 text-[9px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded font-black uppercase">
                    <ShieldCheckIcon size={10} /> {b.driver_name}
                  </span>
                )}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start gap-2 group">
                <div className="flex flex-col items-center mt-1">
                  <div className="w-2 h-2 rounded-full bg-emerald-500" />
                  <div className="w-0.5 h-4 bg-gray-200" />
                  <div className="w-2 h-2 rounded-full bg-rose-500" />
                </div>
                <div className="flex flex-col text-xs space-y-1 w-full overflow-hidden">
                  <p className="text-gray-600 truncate font-medium"><span className="text-gray-400 mr-1">From:</span> {b.pickup_address}</p>
                  <p className="text-gray-600 truncate font-medium"><span className="text-gray-400 mr-1">To:</span> {b.drop_address}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:flex md:flex-row gap-4 md:gap-8 items-center">
              <div className="flex flex-col">
                <span className="text-[10px] uppercase text-gray-400 font-bold tracking-tighter">Date & Time</span>
                <div className="flex items-center gap-1 text-xs font-semibold text-gray-700">
                  <Calendar size={12} className="text-gray-400" />
                  {moment(b.journey_date).format('DD-MM-YYYY')} {b.journey_time}
                </div>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] uppercase text-gray-400 font-bold tracking-tighter">Fare</span>
                <div className="flex items-center gap-0.5 text-sm font-black text-emerald-600">£{b.price}</div>
              </div>
            </div>

            <div className="flex gap-2 self-end md:self-center">
              <button
                onClick={() => openDetails(b, 'view')}
                className="p-2 bg-emerald-50 text-emerald-600 rounded-xl hover:bg-emerald-100 transition-colors"
              >
                <Eye className="w-5 h-5" />
              </button>

              {admin && filter !== 'self' && (
                <button
                  onClick={() => openDetails(b, 'edit')}
                  className="p-2 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-colors"
                >
                  <Edit2 className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        )) : <div className="p-8 text-center text-gray-400 text-sm">No bookings found.</div>}
      </div>

      {totalPages > 1 && (
        <div className="px-4 py-3 border-t border-gray-50 flex items-center justify-between bg-white">
          <span className="text-[10px] md:text-xs font-bold text-gray-400 uppercase tracking-widest">Page {currentPage} of {totalPages}</span>
          <div className="flex gap-2">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(prev => prev - 1)} className="p-2 border rounded-lg disabled:opacity-20 hover:bg-gray-50 transition-colors"><ChevronLeft size={16} /></button>
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(prev => prev + 1)} className="p-2 border rounded-lg disabled:opacity-20 hover:bg-gray-50 transition-colors"><ChevronRight size={16} /></button>
          </div>
        </div>
      )}

      {/* MODAL: EDIT */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white p-6 border-b flex justify-between items-center z-10">
              <h3 className="font-bold text-xl">Edit Booking #{editFormData.id}</h3>
              <button onClick={() => setIsEditModalOpen(false)}><X /></button>
            </div>

            <div className="p-6 space-y-6">
              {/* ADMIN FEATURE: DRIVER ASSIGNMENT */}
              <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-2xl space-y-2">
                <label className="text-[10px] uppercase font-bold text-indigo-600 flex items-center gap-2"><ShieldCheck size={14} /> Assign Driver / Admin</label>
                <select value={editFormData.driver_user_id || "0"} onChange={(e) => setEditFormData({ ...editFormData, driver_user_id: e.target.value })} className="w-full p-3 bg-white border border-indigo-200 rounded-xl font-bold text-gray-700 outline-none">
                  <option value="0">None (Open to all drivers)</option>
                  {drivers.map(d => (
                    <option key={d.id} value={d.id}>
                      {d.name} ({d.user_type == 1 ? "Admin" : "Driver"})
                    </option>
                  ))}
                </select>
              </div>

              {/* TRIP TYPE SELECTION */}
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setEditFormData({ ...editFormData, airport_trip: 0 })}
                  className={cn(
                    "flex items-center justify-center gap-2 p-3 rounded-xl border font-bold text-xs transition-all",
                    editFormData.airport_trip == 0 ? "bg-emerald-600 border-emerald-600 text-white shadow-md shadow-emerald-100" : "bg-white border-gray-200 text-gray-600"
                  )}
                >
                  <Home size={16} /> Local Job
                </button>
                <button
                  type="button"
                  onClick={() => setEditFormData({ ...editFormData, airport_trip: 1 })}
                  className={cn(
                    "flex items-center justify-center gap-2 p-3 rounded-xl border font-bold text-xs transition-all",
                    editFormData.airport_trip == 1 ? "bg-indigo-600 border-indigo-600 text-white shadow-md shadow-indigo-100" : "bg-white border-gray-200 text-gray-600"
                  )}
                >
                  <Plane size={16} /> Airport Trip
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <EditField label="Name" value={editFormData.name} onChange={v => setEditFormData({ ...editFormData, name: v })} />
                <EditField label="Phone" value={editFormData.phone_number} onChange={v => setEditFormData({ ...editFormData, phone_number: v })} />
                <EditField label="Email" value={editFormData.email} onChange={v => setEditFormData({ ...editFormData, email: v })} />
                <EditField label="Date" type="date" value={editFormData.journey_date} onChange={v => setEditFormData({ ...editFormData, journey_date: v })} />
                <EditField label="Time" type="time" value={editFormData.journey_time} onChange={v => setEditFormData({ ...editFormData, journey_time: v })} />
              </div>

              <div className="space-y-4 bg-gray-50 p-4 rounded-2xl border border-gray-100">
                <label className="text-[10px] uppercase font-bold text-gray-400">Pickup Location</label>
                <GoogleInput value={editFormData.pickup_address} className="px-4 py-2 rounded-xl border border-gray-200 w-full bg-white" onSelect={(p) => setEditFormData({ ...editFormData, pickup_address: p.address, pickup_lat: p.lat, pickup_lng: p.lng })} />

                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <label className="text-[10px] uppercase font-bold text-gray-400">Intermediate Stops</label>
                    <button onClick={addPoint} className="text-[10px] bg-emerald-600 text-white px-2 py-1 rounded flex items-center gap-1"><Plus className="w-3 h-3" /> Add Stop</button>
                  </div>
                  {bookingPoints.map((point, index) => (
                    <div key={index} className="flex gap-2">
                      <GoogleInput value={point.address} className="flex-1 px-3 py-2 border rounded-lg text-sm bg-white" onSelect={(p) => {
                        const updated = [...bookingPoints];
                        updated[index] = { ...updated[index], address: p.address, lat: p.lat, lng: p.lng };
                        setBookingPoints(updated);
                      }} />
                      <button onClick={() => removePoint(index)} className="p-2 text-rose-500 hover:bg-rose-100 rounded-lg"><Trash2 size={16} /></button>
                    </div>
                  ))}
                </div>

                <label className="text-[10px] uppercase font-bold text-gray-400">Drop Location</label>
                <GoogleInput value={editFormData.drop_address} className="px-4 py-2 rounded-xl border border-gray-200 w-full bg-white" onSelect={(p) => setEditFormData({ ...editFormData, drop_address: p.address, drop_lat: p.lat, drop_lng: p.lng })} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-[10px] uppercase font-bold text-gray-400">Distance (Miles)</label>
                  <input readOnly value={editFormData.distance} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-100 outline-none" />
                </div>
                <EditField label="Price (£)" type="number" value={editFormData.price} onChange={v => setEditFormData({ ...editFormData, price: v })} />
              </div>

              <div className="flex items-center gap-3 bg-gray-50 p-4 rounded-2xl border cursor-pointer" onClick={() => setEditFormData({ ...editFormData, is_return: editFormData.is_return === 1 ? 0 : 1 })}>
                <div className={cn("w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all", editFormData.is_return === 1 ? "bg-emerald-600 border-emerald-600" : "bg-white border-gray-300")}>
                  {editFormData.is_return === 1 && <CheckCircle className="w-4 h-4 text-white" />}
                </div>
                <label className="text-sm font-bold text-gray-700 cursor-pointer select-none">Return Journey Included</label>
              </div>

              <button onClick={handleUpdate} className="w-full bg-emerald-600 text-white py-4 rounded-2xl font-bold flex items-center justify-center gap-2 hover:bg-emerald-700 transition-all"><Save className="w-5 h-5" /> Save Changes</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: VIEW SUMMARY */}
      {selectedBooking && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl">
            <div className="p-6 border-b flex justify-between items-center bg-gray-50">
              <h3 className="font-bold text-xl">
                {filter === 'self' ? 'Trip Operation' : 'Booking Summary'}
              </h3>
              <button onClick={() => setSelectedBooking(null)}><X /></button>
            </div>

            <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">

              {/* 1. TRIP & ASSIGNMENT OVERVIEW (Common) */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-indigo-50 border border-indigo-100 rounded-xl text-center">
                  <p className="text-[10px] uppercase font-bold text-indigo-400 mb-1">Assigned Driver</p>
                  <p className="text-sm font-bold text-indigo-700 flex items-center justify-center gap-2">
                    <CarTaxiFront size={14} /> {filter === 'self' ? user.name : selectedBooking.driver_name || "None"}
                  </p>
                </div>
                <div className={cn(
                  "p-3 border rounded-xl text-center",
                  getTripStatusLabel(selectedBooking.trip_sub_status || selectedBooking.trip_status_val).color
                )}>
                  <p className="text-[10px] uppercase font-bold opacity-60 mb-1">Live Trip Status</p>
                  <p className="text-sm font-black uppercase tracking-tighter">
                    {getTripStatusLabel(selectedBooking.trip_sub_status || selectedBooking.trip_status_val).label}
                  </p>

                  {/* Display OTPs if trip is active */}
                  {(selectedBooking.trip_sub_status == 1 || selectedBooking.trip_status_val == 1) && selectedBooking.OTP && (
                    <div className="mt-2 pt-1 border-t border-blue-200 w-full flex items-center justify-center gap-1.5">
                      <KeyRound size={12} className="text-blue-500" />
                      <span className="text-xs font-black text-blue-700">OTP: {selectedBooking.OTP}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* 2. CUSTOMER & TRIP INFO (Common) */}
              <div className="grid grid-cols-2 gap-y-4 gap-x-2 bg-emerald-50/50 p-4 rounded-2xl border border-emerald-100/50">
                <div className="col-span-1">
                  <span className="text-gray-400 block uppercase text-[10px] font-black tracking-wider">Customer Name</span>
                  <p className="font-bold text-gray-900 flex items-center gap-2"><User size={14} className="text-emerald-600" /> {selectedBooking.name}</p>
                </div>
                <div className="col-span-1">
                  <span className="text-gray-400 block uppercase text-[10px] font-black tracking-wider">Customer Email</span>
                  <p className="font-bold text-gray-900 truncate flex items-center gap-2"><Mail size={14} className="text-emerald-600" /> {selectedBooking.email}</p>
                </div>
                <div>
                  <span className="text-gray-400 block uppercase text-[10px] font-black tracking-wider">Phone</span>
                  <a href={`tel:${selectedBooking.phone_number}`} className="text-sm font-semibold text-gray-900 flex items-center gap-1.5 hover:text-emerald-600">
                    <Phone size={14} className="text-emerald-600" /> {selectedBooking.phone_number}
                  </a>
                </div>
                <div>
                  <span className="text-gray-400 block uppercase text-[10px] font-black tracking-wider">Trip Type</span>
                  <p className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
                    {selectedBooking.airport_trip == 1 ? <><Plane size={14} className="text-indigo-600" /> Airport</> : <><Home size={14} className="text-emerald-600" /> Local</>}
                  </p>
                </div>
                <div>
                  <span className="text-gray-400 block uppercase text-[10px] font-black tracking-wider">Date</span>
                  <p className="text-sm font-semibold text-gray-900 flex items-center gap-1.5"><Calendar size={14} /> {moment(selectedBooking.journey_date).format('DD-MM-YYYY')}</p>
                </div>
                <div>
                  <span className="text-gray-400 block uppercase text-[10px] font-black tracking-wider">Time</span>
                  <p className="text-sm font-semibold text-gray-900 flex items-center gap-1.5"><Timer size={14} /> {selectedBooking.journey_time}</p>
                </div>
              </div>

              {/* 3. ROUTE (Common) */}
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 space-y-3">
                {/* CLICKABLE PICKUP */}
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedBooking.pickup_address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex gap-2 group cursor-pointer"
                >
                  <MapPin className="text-emerald-500 w-4 h-4 mt-0.5 group-hover:scale-110 transition-transform" />
                  <div>
                    <p className="text-[10px] text-emerald-600 font-bold uppercase">Pickup</p>
                    <p className="text-sm font-medium group-hover:text-emerald-700 group-hover:underline decoration-emerald-200 transition-all">
                      {selectedBooking.pickup_address}
                    </p>
                  </div>
                </a>

                {/* CLICKABLE INTERMEDIATE STOPS */}
                {bookingPoints.map((p, i) => (
                  <a
                    key={i}
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(p.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex gap-2 pl-4 border-l-2 border-dashed ml-2 group cursor-pointer hover:border-emerald-400 transition-colors"
                  >
                    <p className="text-xs text-gray-600 italic group-hover:text-emerald-600 transition-colors">
                      Stop: {p.address}
                    </p>
                  </a>
                ))}

                {/* CLICKABLE DROP-OFF */}
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedBooking.drop_address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex gap-2 group cursor-pointer"
                >
                  <MapPin className="text-rose-500 w-4 h-4 mt-0.5 group-hover:scale-110 transition-transform" />
                  <div>
                    <p className="text-[10px] text-rose-600 font-bold uppercase">Drop-off</p>
                    <p className="text-sm font-medium group-hover:text-rose-700 group-hover:underline decoration-rose-200 transition-all">
                      {selectedBooking.drop_address}
                    </p>
                  </div>
                </a>
              </div>

              {/* 4. TOTAL & STATUS (Common) */}
              <div className="flex justify-between items-center border-t pt-4">
                <div className="flex flex-col">
                  <span className="text-[10px] text-gray-400 font-bold uppercase">Total Fare</span>
                  <span className="font-bold text-2xl text-emerald-700">£{selectedBooking.price}</span>
                </div>
                <StatusBadge status={selectedBooking.status} />
              </div>

              {/* 5. DYNAMIC FOOTER ACTIONS */}
              <div className="mt-4 flex gap-2">
                {filter === 'self' ? (
                  // If viewing Admin's own trip, show Driver Dashboard style buttons
                  renderDriverButtons()
                ) : (
                  // Standard Admin Workflow
                  selectedBooking.status === 0 ? (
                    <>
                      <button onClick={() => handleStatus(selectedBooking.id, 1)} className="flex-1 py-3 bg-emerald-600 text-white rounded-xl font-bold">Approve</button>
                      <button onClick={() => handleStatus(selectedBooking.id, 2)} className="flex-1 py-3 bg-rose-600 text-white rounded-xl font-bold">Reject</button>
                    </>
                  ) : (
                    (!selectedBooking.assigned_id || selectedBooking.assigned_id == 0) && (
                      <button onClick={() => handleStatus(selectedBooking.id, 0)} className="w-full py-3 bg-gray-800 text-white rounded-xl font-bold flex items-center justify-center gap-2">
                        <RotateCcw size={18} /> Revoke to Pending
                      </button>
                    )
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- DRIVER WORKFLOW MODALS (Ported from DriverDashboard) --- */}
      {showStartModal && (
        <div className="fixed inset-0 bg-black/60 z-[110] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white p-6 rounded-2xl w-full max-w-sm">
            <h3 className="text-lg font-bold mb-1">Arrival Duration</h3>
            <div className="flex gap-4 my-6">
              <select value={selectedHours} onChange={(e) => setSelectedHours(parseInt(e.target.value))} className="flex-1 p-3 bg-gray-50 border rounded-xl font-bold">
                {[0, 1, 2].map(h => <option key={h} value={h}>{h} hr</option>)}
              </select>
              <select value={selectedMinutes} onChange={(e) => setSelectedMinutes(parseInt(e.target.value))} className="flex-1 p-3 bg-gray-50 border rounded-xl font-bold">
                {[5, 10, 15, 20, 30, 45].map(m => <option key={m} value={m}>{m} min</option>)}
              </select>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowStartModal(false)} className="flex-1 py-3 font-bold text-gray-400">Cancel</button>
              <button onClick={() => setShowConfirmStart(true)} className="flex-[2] py-3 bg-emerald-600 text-white rounded-xl font-bold">Start</button>
            </div>
          </div>
        </div>
      )}

      {showConfirmStart && (
        <div className="fixed inset-0 bg-black/80 z-[120] flex items-center justify-center p-4">
          <div className="bg-white p-6 rounded-2xl w-full max-w-xs text-center">
            <ShieldCheck className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
            <h3 className="text-lg font-bold mb-2">Confirm Start?</h3>
            <p className="text-sm text-gray-500 mb-6">Are you sure you want to start the journey and notify the customer?</p>
            <div className="flex gap-3">
              <button onClick={() => setShowConfirmStart(false)} className="flex-1 py-2 font-bold">No</button>
              <button onClick={handleStartJourney} className="flex-1 py-2 bg-emerald-600 text-white rounded-lg font-bold">Confirm</button>
            </div>
          </div>
        </div>
      )}
      {showOtpModal && (
        <div className="fixed inset-0 bg-black/60 z-[110] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white p-6 rounded-2xl w-full max-w-sm relative shadow-2xl">
            {/* Top Right Close Button */}
            <button
              onClick={() => setShowOtpModal(false)}
              className="absolute right-4 top-4 p-1.5 hover:bg-gray-100 rounded-full transition-colors text-gray-400"
            >
              <X size={20} />
            </button>

            <h3 className="text-lg font-bold mb-1 text-center">Enter Pickup OTP</h3>
            <p className="text-xs text-gray-500 text-center mb-6">Ask the customer for the 4-digit code</p>

            <input
              type="text"
              maxLength={4}
              placeholder="0000"
              className="w-full text-center text-3xl font-black p-4 border-2 border-emerald-100 bg-emerald-50/30 rounded-xl mb-6 focus:border-emerald-500 outline-none transition-all tracking-widest"
              value={otpInput}
              onChange={(e) => setOtpInput(e.target.value)}
            />

            <div className="flex gap-3">
              <button
                onClick={() => setShowOtpModal(false)}
                className="flex-1 py-3 text-gray-500 font-bold hover:bg-gray-50 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleVerifyOTP}
                disabled={otpInput.length < 4}
                className="flex-[2] py-3 bg-emerald-600 text-white rounded-xl font-bold hover:bg-emerald-700 shadow-lg shadow-emerald-100 disabled:opacity-50 disabled:shadow-none transition-all"
              >
                Verify & Start Trip
              </button>
            </div>
          </div>
        </div>
      )}

      {showCloseTripModal && (
        <div className="fixed inset-0 bg-black/60 z-[110] flex items-center justify-center p-4">
          <div className="bg-white p-6 rounded-2xl w-full max-w-md shadow-2xl">
            <h3 className="text-xl font-bold mb-4">Close Trip</h3>
            <div className="space-y-4 mb-6">
              <input type="number" value={editablePrice} onChange={(e) => setEditablePrice(parseFloat(e.target.value))} className="w-full p-4 bg-gray-50 border rounded-xl font-bold text-2xl" />
              <div className="p-4 rounded-xl border-2 bg-amber-50 border-amber-200">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-bold text-amber-700">Use Loyalty Points? ({pointsBalance})</span>
                  <input type="checkbox" checked={useLoyalty} onChange={(e) => setUseLoyalty(e.target.checked)} className="w-6 h-6" />
                </div>
                {useLoyalty && (
                  <input
                    type="number"
                    value={pointsToUse}
                    onChange={(e) => setPointsToUse(Math.min(pointsBalance, parseInt(e.target.value) || 0))}
                    className="w-full p-2 border rounded"
                    placeholder="Enter points"
                  />
                )}
              </div>
              <div className="p-4 bg-gray-900 rounded-xl text-white flex justify-between">
                <span className="font-bold">Collect:</span>
                <span className="text-2xl font-black text-emerald-400">£{(editablePrice - (useLoyalty ? pointsToUse / 100 : 0)).toFixed(2)}</span>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowCloseTripModal(false)} className="flex-1 py-4 font-bold text-gray-400">Cancel</button>
              <button onClick={handleCloseTripSubmit} className="flex-[2] py-4 bg-rose-600 text-white rounded-xl font-bold">Send Closing OTP</button>
            </div>
          </div>
        </div>
      )}

      {showClosingOtpModal && (
        <div className="fixed inset-0 bg-black/60 z-[110] flex items-center justify-center p-4">
          <div className="bg-white p-6 rounded-2xl w-full max-w-sm text-center">
            <h3 className="text-lg font-bold mb-4">Enter Closing OTP</h3>
            <input type="text" maxLength={4} className="w-full text-center text-3xl font-bold p-4 border rounded-xl mb-4" value={closingOtpInput} onChange={(e) => setClosingOtpInput(e.target.value)} />

            <div className="flex gap-3">
              <button onClick={handleVerifyClosingOTP} className="w-50 py-3 bg-emerald-600 text-white rounded-xl font-bold">Complete Trip</button>
              <button onClick={() => setShowClosingOtpModal(false)} className="flex-1 py-4 font-bold text-gray-400" style={{border:'1px solid' ,borderRadius:'10px'}}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


const EditField = ({ label, value, onChange, type = "text" }) => (
  <div className="space-y-1">
    <label className="text-[10px] uppercase font-bold text-gray-400">{label}</label>
    <input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none bg-white" />
  </div>
);

const StatusBadge = ({ status }) => {
  const config = {
    0: { label: 'Pending', classes: 'bg-amber-50 text-amber-700' },
    1: { label: 'Approved', classes: 'bg-emerald-50 text-emerald-700' },
    2: { label: 'Rejected', classes: 'bg-rose-50 text-rose-700' },
  };
  const { label, classes } = config[status] || config[0];
  return <span className={cn("px-2 py-0.5 rounded-full text-[9px] font-bold uppercase w-fit", classes)}>{label}</span>;
}